import * as React from 'react';

import { runtimeConfig } from '~/config/runtime';

type SidecarLatestVersion = {
  latestVersion: string | null;
  downloadUrl: string | null;
  isLoading: boolean;
};

function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

export function useSidecarLatestVersion(): SidecarLatestVersion {
  const [state, setState] = React.useState<SidecarLatestVersion>({
    latestVersion: null,
    downloadUrl: null,
    isLoading: true,
  });

  React.useEffect(() => {
    const controller = new AbortController();

    const baseUrl = runtimeConfig.api.url.replace(/\/$/, '');
    const manifestUrl = `${baseUrl}/rematter/sidecar/manifest.yaml`;

    fetch(manifestUrl, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        const tagMatches = [...text.matchAll(/tag_name:\s*(\S+)/g)];
        if (tagMatches.length === 0) {
          setState({
            latestVersion: null,
            downloadUrl: null,
            isLoading: false,
          });
          return;
        }

        const versions = tagMatches.map((m) => m[1]);
        versions.sort(compareSemver);
        const latest = versions[versions.length - 1];

        const downloadUrl = `${baseUrl}/rematter/sidecar/${latest}/sidecar_${latest}_windows_amd64.zip`;

        setState({ latestVersion: latest, downloadUrl, isLoading: false });
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setState({
            latestVersion: null,
            downloadUrl: null,
            isLoading: false,
          });
        }
      });

    return () => controller.abort();
  }, []);

  return state;
}

export function isVersionOutdated(
  currentVersion: string | null | undefined,
  latestVersion: string | null,
): boolean {
  if (!currentVersion || !latestVersion) return false;
  return compareSemver(currentVersion, latestVersion) < 0;
}
